import React, { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import forumStoreService from '@/lib/services/forumStore.service'

interface MoveTopicModalProps {
  isOpen: boolean
  onClose: () => void
  onMove: (targetBoardId: string) => Promise<void>
  currentBoardId: string
  topicId: string
  storeId: string
}

interface Board {
  id: string
  name: string
  description: string | null
  categoryId: string
  categoryName: string
}

export function MoveTopicModal({
  isOpen,
  onClose,
  onMove,
  currentBoardId,
  topicId,
  storeId,
}: MoveTopicModalProps) {
  const [boards, setBoards] = useState<Board[]>([])
  const [selectedBoardId, setSelectedBoardId] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadBoards()
    }
  }, [isOpen])

  async function loadBoards() {
    setIsLoading(true)
    try {
      const categories = await forumStoreService.listCategories(storeId)
      const allBoards: Board[] = []
      
      for (const category of categories) {
        for (const board of category.boards) {
          if (board.id !== currentBoardId) {
            allBoards.push({
              id: board.id,
              name: board.name,
              description: board.description,
              categoryId: category.id,
              categoryName: category.name,
            })
          }
        }
      }
      
      setBoards(allBoards)
    } catch (error) {
      console.error('Erro ao carregar boards:', error)
    } finally {
      setIsLoading(false)
    }
  }

  async function handleMove() {
    if (!selectedBoardId) {
      alert('Selecione um board de destino')
      return
    }

    setIsSubmitting(true)
    try {
      await onMove(selectedBoardId)
      onClose()
    } catch (error) {
      // Error is handled by parent
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-xl font-semibold text-card-foreground">
            Mover Tópico
          </h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4">
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Carregando boards...
            </div>
          ) : boards.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum outro board disponível
            </div>
          ) : (
            <>
              <label className="block text-sm font-medium text-foreground mb-2">
                Selecione o board de destino:
              </label>
              <select
                value={selectedBoardId}
                onChange={(e) => setSelectedBoardId(e.target.value)}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground"
              >
                <option value="">Selecione um board...</option>
                {boards.map((board) => (
                  <option key={board.id} value={board.id}>
                    {board.categoryName} → {board.name}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleMove}
            disabled={isSubmitting || !selectedBoardId || isLoading}
          >
            {isSubmitting ? 'Movendo...' : 'Mover'}
          </Button>
        </div>
      </div>
    </div>
  )
}
